/**
 * Browse-discovery add-to-cart — pure localStorage helper (Stage 7 persistent multi-cart).
 *
 * Browse runs under `__platform__` chrome, so callers must not use
 * `useShopCart` (that hook keys off TenantProvider). This writes the offer's
 * real tenant + sales-point cart key instead.
 */

import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { isPaymentPending } from '../../app/pwa/paymentPendingGate.js';
import type { PublicDiscoveryOfferItemDTO } from '../discovery/publicDiscoveryTypes.js';
import { certifyAgeGate } from '../mor-chrome/ageGateModel.js';
import { resolveDiscoveryOfferDisplayImageUrl } from './browseOfferPresentation.js';
import { addLine } from '../shop/cart/cartLogic.js';
import { loadCart, saveCart } from '../shop/cart/cartStorage.js';
import {
  rememberSalesPointForTenant,
  rememberSalesPointIdForTenant,
} from '../../shared/session/lastKnownSalesPoint.js';

export type DiscoveryOfferAgeGateInput = {
  readonly reviewRisk: string | null | undefined;
  /** false while seller-legal for the offer tenant is still loading / failed. */
  readonly sellerLegalResolved: boolean;
};

export type AddDiscoveryOfferToCartResult =
  | { ok: true; tenantCode: string; kioskId: number; totalQuantity: number }
  | {
      ok: false;
      reason:
        | 'price_unavailable'
        | 'invalid_sales_point'
        | 'payment_pending'
        | 'storage_failed'
        | 'empty_description'
        | 'age_gate_unresolved'
        | 'age_gate_required';
    };

function isValidSalesPointId(salesPointId: number): boolean {
  return Number.isFinite(salesPointId) && salesPointId > 0;
}

export function addDiscoveryOfferToCart(
  offer: PublicDiscoveryOfferItemDTO,
  ageGate: DiscoveryOfferAgeGateInput,
): AddDiscoveryOfferToCartResult {
  if (offer.price === null) {
    return { ok: false, reason: 'price_unavailable' };
  }

  // GoPay MoR — HARD BLOCK empty Product.description for directory offers.
  if (typeof offer.description !== 'string' || offer.description.trim().length === 0) {
    return { ok: false, reason: 'empty_description' };
  }

  const tenantCode = offer.tenantContext.tenantCode.trim();
  const salesPointId = offer.salesPointContext.salesPointId;

  if (
    tenantCode.length === 0 ||
    isPlatformChromeTenant(tenantCode) ||
    !isValidSalesPointId(salesPointId)
  ) {
    return { ok: false, reason: 'invalid_sales_point' };
  }

  // GoPay MoR G5/G11 — fail-closed 18+ before browse add (offer ageRestricted ∪ ELEVATED).
  const ageCert = certifyAgeGate({
    tenantCode,
    products: [{ ageRestricted: offer.ageRestricted === true }],
    catalogResolved: true,
    reviewRisk: ageGate.reviewRisk,
    sellerLegalResolved: ageGate.sellerLegalResolved,
  });
  if (!ageCert.ok) {
    if (ageCert.reason === 'unresolved') {
      return { ok: false, reason: 'age_gate_unresolved' };
    }
    if (ageCert.reason === 'not_acknowledged') {
      return { ok: false, reason: 'age_gate_required' };
    }
    return { ok: false, reason: 'age_gate_required' };
  }

  if (isPaymentPending()) {
    return { ok: false, reason: 'payment_pending' };
  }

  const kioskId = Math.floor(salesPointId);
  const price = offer.price;
  const { productId, variantId } = offer.productContext;

  const cart = loadCart(tenantCode, kioskId);
  const next = addLine(cart, {
    product: {
      id: productId,
      name: offer.title,
      price,
      imageUrl: resolveDiscoveryOfferDisplayImageUrl(offer),
    },
    variant:
      variantId !== null
        ? {
            id: variantId,
            name: null,
            price,
            sku: null,
            barcode: null,
          }
        : null,
    unitPrice: price,
  });
  const saveResult = saveCart(tenantCode, kioskId, next, {
    tenantName: offer.tenantContext.tenantName,
    salesPointLabel: offer.salesPointContext.name,
    shopHref: offer.salesPointContext.customerShopUrl,
  });
  if (!saveResult.ok) {
    return { ok: false, reason: 'storage_failed' };
  }

  rememberSalesPointIdForTenant(tenantCode, kioskId);
  rememberSalesPointForTenant(tenantCode, kioskId, offer.salesPointContext.code);

  return {
    ok: true,
    tenantCode,
    kioskId,
    totalQuantity: next.totalQuantity,
  };
}
